import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowLeft, RefreshCw, KeyRound, Lock } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { AuthLayout, Field } from "@/pages/auth/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function ForgotPassword() {
  const { user, loading: authLoading, sendForgotPasswordOtp, resetPassword } = useAuth();
  const navigate = useNavigate();
  usePageTitle("Reset Password");

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Timers
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(600); // 10 minutes

  // Auto-redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Expiry countdown
  useEffect(() => {
    if (step !== 2 || expiresIn <= 0) return;
    const timer = setInterval(() => {
      setExpiresIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, expiresIn]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleSendResetCode = async (e) => {
    if (e) e.preventDefault();
    setError("");
    if (!email.trim()) return setError("Enter your email address.");

    setLoading(true);
    try {
      await sendForgotPasswordOtp(email.trim());
      toast.success(`Verification code sent to ${email.trim()}`);
      setStep(2);
      setCooldown(60);
      setExpiresIn(600);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (cooldown > 0 || loading) return;
    setError("");
    setLoading(true);
    try {
      await sendForgotPasswordOtp(email.trim());
      toast.success("A new verification code has been dispatched.");
      setCooldown(60);
      setExpiresIn(600);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    const cleanOtp = otp.trim();
    if (cleanOtp.length !== 6) return setError("Enter the 6-digit verification code.");
    if (newPassword.length < 8) return setError("Password must be at least 8 characters.");
    if (newPassword !== confirmPassword) return setError("Passwords do not match.");

    setLoading(true);
    try {
      await resetPassword(email.trim(), cleanOtp, newPassword);
      toast.success("Password reset successfully! Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={step === 1 ? "Reset password" : "Create new password"}
      subtitle={
        step === 1
          ? "Enter your account email to receive a 6-digit verification code."
          : `Enter the 6-digit code sent to ${email} and choose a new password.`
      }
      footer={
        step === 1 ? (
          <>
            Remember your password?{" "}
            <Link to="/login" className="font-semibold text-accent hover:underline" data-testid="link-login">
              Log in
            </Link>
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setError("");
            }}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Change email address
          </button>
        )
      }
    >
      {step === 1 ? (
        <form onSubmit={handleSendResetCode} className="space-y-4" data-testid="forgot-password-form">
          <Field label="Email Address">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="forgot-email"
              autoComplete="email"
              required
            />
          </Field>

          {error && (
            <p className="rounded-xl bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="forgot-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit"
            className="w-full rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm"
          >
            {loading ? "Sending reset code…" : "Send Reset Code"}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
          <div className="rounded-2xl border border-border/70 bg-card/60 p-4 text-center">
            <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent/10 text-accent">
              <Mail className="h-5 w-5" />
            </div>
            <p className="text-xs text-muted-foreground">
              Code sent to <strong className="text-foreground">{email}</strong>
            </p>
            <div className="mt-2 text-xs font-medium text-accent">
              Code expires in {formatTime(expiresIn)}
            </div>
          </div>

          <Field label="6-Digit Reset Code">
            <div className="relative">
              <KeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                }}
                placeholder="123456"
                data-testid="reset-otp"
                autoFocus
                autoComplete="one-time-code"
                className="pl-10 text-center font-mono text-xl tracking-[0.35em] font-bold"
              />
            </div>
          </Field>

          <Field label="New Password">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                data-testid="reset-new-password"
                autoComplete="new-password"
                className="pl-10"
                required
              />
            </div>
          </Field>

          <Field label="Confirm New Password">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                data-testid="reset-confirm-password"
                autoComplete="new-password"
                className="pl-10"
                required
              />
            </div>
          </Field>

          {error && (
            <p className="rounded-xl bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="reset-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || otp.length !== 6 || !newPassword || !confirmPassword}
            data-testid="reset-submit"
            className="w-full rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm"
          >
            {loading ? "Resetting password…" : "Reset Password & Log In"}
          </Button>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">Didn't receive the code?</span>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={cooldown > 0 || loading}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                cooldown > 0 ? "text-muted-foreground/60 cursor-not-allowed" : "text-accent hover:underline"
              }`}
            >
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
            </button>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
